/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useReducer, useEffect, useCallback, useRef, useState } from 'react'

import axios, { isAxiosError } from 'axios'

import { Loader } from '@/components/ui'
import { setUserTimezone } from '@/insights/common/timezone'
import { isInsightAllowed as isInsightAllowedRule } from '@/insights/insightAccess'
import { clearToken, saveToken } from '@/services/authService'
import { GeotabSessionError, requestGeotabSession } from '@/services/geotabSession'

import { registerNeedsHardLoginCallback, unregisterNeedsHardLoginCallback } from './apiService'
import type { AuthContextValue, AppJwtPayload } from './auth-types'
import {
  AuthContext,
  authReducer,
  buildUserInfo,
  extractInsightsFromToken,
  extractPermissionsFromToken,
  initialState,
} from './authContext'

/**
 * Auth strategy for running as a MyGeotab add-in.
 *
 * MyGeotab is the identity provider: the add-in shim hands us a live session, the backend
 * replays it to Geotab to prove it is real, and we get back the same app JWT the Auth0
 * login produces. Auth0 is never loaded in the browser here — which is the point, since
 * its login page sets `frame-ancestors 'none'` and cannot be shown in an iframe.
 *
 * There is no screensaver in this mode. An active user is kept alive by the server's
 * sliding `X-Refreshed-Token` refresh; an idle one simply 401s on the next request and is
 * silently re-authenticated from the still-live MyGeotab session.
 */

/** What went wrong, mapped to a message at render time. */
type GeotabFailure = 'not-authorized' | 'session-ended' | 'unavailable' | 'signed-out'

interface GeotabTokenResponse {
  token: string
  user: {
    sub: string
    picture?: string
    user_metadata: NonNullable<AppJwtPayload['user_metadata']>
  }
}

/**
 * A silent re-auth that immediately 401s again would spin forever. Allow a small number of
 * attempts per page load, then fall back to the error panel and its Retry button.
 */
const MAX_SILENT_REAUTHS = 3

const FAILURE_MESSAGES: Record<GeotabFailure, { title: string; body: string; canRetry: boolean }> = {
  'not-authorized': {
    title: 'Not authorized',
    body: 'You are not authorized to access this page, please contact your administrator.',
    canRetry: false,
  },
  'session-ended': {
    title: 'MyGeotab session ended',
    body: 'Your MyGeotab session is no longer active. Refresh MyGeotab and reopen this page to continue.',
    canRetry: true,
  },
  unavailable: {
    title: 'Unable to sign in',
    body: 'Cooltrax Insights could not be reached. Please try again in a moment.',
    canRetry: true,
  },
  'signed-out': {
    title: 'Signed out',
    body: 'You have been signed out. Reopen this page from MyGeotab to continue.',
    canRetry: true,
  },
}

const serverUrl = () =>
  window.VITE_MEGALODON_SERVER_URL || import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3000'

/**
 * Translate a failure into the message the user should see. The backend deliberately
 * returns the same 401 for every authorization failure, so there is nothing finer to say.
 */
const classifyFailure = (err: unknown): GeotabFailure => {
  if (err instanceof GeotabSessionError) {
    // 'timeout' and 'not-embedded' also land here: from the user's point of view the
    // add-in frame isn't talking to us, and refreshing MyGeotab is the right advice.
    return 'session-ended'
  }
  if (isAxiosError(err)) {
    if (err.response?.status === 401 || err.response?.status === 403) return 'not-authorized'
    return 'unavailable'
  }
  return 'unavailable'
}

export const GeotabAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const inFlightRef = useRef(false)
  // Drives the retry button. The error panel keeps rendering while a retry runs, so without
  // this the button looks inert for however long the exchange takes.
  const [isRetrying, setIsRetrying] = useState(false)
  const silentReauthsRef = useRef(0)
  const failureRef = useRef<GeotabFailure | null>(null)

  // Register the 401 callback so a dead app token surfaces as needsHardLogin, exactly as
  // it does for the Auth0 strategy. What we do about it differs — see below.
  useEffect(() => {
    registerNeedsHardLoginCallback(() => dispatch({ type: 'SET_NEEDS_HARD_LOGIN', needsHardLogin: true }))
    return () => unregisterNeedsHardLoginCallback()
  }, [])

  // Publish the profile timezone for non-React code, same as AuthProvider.
  useEffect(() => {
    setUserTimezone(state.user?.timezone)
  }, [state.user?.timezone])

  /**
   * Ask the shim for a live MyGeotab session and exchange it for an app token.
   * The session is held only for the duration of this call and never stored.
   */
  const authenticate = useCallback(async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    try {
      const session = await requestGeotabSession()
      const { data } = await axios.post<GeotabTokenResponse>(`${serverUrl()}/auth/geotab`, session, {
        timeout: 20_000,
        headers: { 'Content-Type': 'application/json' },
      })

      if (!data?.token) {
        throw new Error('Geotab token exchange returned no token')
      }

      saveToken(data.token)
      failureRef.current = null

      const permissions = extractPermissionsFromToken(data.token)
      const insights = extractInsightsFromToken(data.token)
      const user = buildUserInfo(data.user.user_metadata, data.user.sub, data.user.picture)

      dispatch({ type: 'SET_AUTHENTICATED', token: data.token, user, originalUser: null, permissions, insights })
      dispatch({ type: 'SET_NEEDS_HARD_LOGIN', needsHardLogin: false })
    } catch (err) {
      const failure = classifyFailure(err)
      failureRef.current = failure
      console.error('Geotab authentication failed (%s):', failure, err)
      clearToken()
      dispatch({ type: 'SET_INITIALIZED_ERROR', error: failure })
    } finally {
      inFlightRef.current = false
    }
  }, [])

  // Initial sign-in.
  const didInitRef = useRef(false)
  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true
    void authenticate()
  }, [authenticate])

  // A 401 means the app token died while the MyGeotab session is very likely still alive,
  // so re-auth silently instead of prompting — there is no login UI we could show in the
  // frame anyway.
  useEffect(() => {
    if (!state.needsHardLogin) return
    if (inFlightRef.current) return
    if (silentReauthsRef.current >= MAX_SILENT_REAUTHS) {
      failureRef.current = 'session-ended'
      dispatch({ type: 'SET_INITIALIZED_ERROR', error: 'session-ended' })
      return
    }
    silentReauthsRef.current += 1
    void authenticate()
  }, [state.needsHardLogin, authenticate])

  // --- Actions ---

  const setNeedsHardLogin = useCallback((v: boolean) => {
    dispatch({ type: 'SET_NEEDS_HARD_LOGIN', needsHardLogin: v })
  }, [])

  const hasPermission = useCallback(
    (p: string): boolean => {
      const perms = state.permissions
      if (p.endsWith('*')) {
        const prefix = p.slice(0, -1)
        return perms.some((perm) => perm.startsWith(prefix))
      }
      return perms.includes(p)
    },
    [state.permissions]
  )

  const isInsightAllowed = useCallback((slug: string) => isInsightAllowedRule(state.insights, slug), [state.insights])

  const hasAnyPermission = useCallback(
    (ps: string): boolean => {
      const perms = state.permissions
      return ps.split(/\s+/).some((p) => perms.includes(p))
    },
    [state.permissions]
  )

  /**
   * No Auth0 session to end and no redirect target that makes sense inside the frame —
   * just drop our token. MyGeotab owns the real sign-out.
   */
  const logout = useCallback(async () => {
    clearToken()
    failureRef.current = 'signed-out'
    dispatch({ type: 'SET_INITIALIZED_ERROR', error: 'signed-out' })
  }, [])

  const refreshToken = useCallback(async () => {
    await authenticate()
  }, [authenticate])

  const value: AuthContextValue = {
    token: state.token,
    user: state.user,
    originalUser: state.originalUser,
    permissions: state.permissions,
    insights: state.insights,
    isAuthenticated: !!state.token,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    needsHardLogin: state.needsHardLogin,
    hasPermission,
    hasAnyPermission,
    isInsightAllowed,
    setNeedsHardLogin,
    logout,
    refreshToken,
  }

  if (!state.isInitialized) {
    return (
      <AuthContext.Provider value={value}>
        <Loader fullScreen />
      </AuthContext.Provider>
    )
  }

  if (state.error && !state.token) {
    const failure = FAILURE_MESSAGES[(failureRef.current ?? 'unavailable') as GeotabFailure]
    return (
      <AuthContext.Provider value={value}>
        <div className="flex h-screen items-center justify-center p-4">
          <div className="card w-full max-w-md bg-base-100 shadow-xl">
            <div className="card-body items-center text-center">
              <h2 className="card-title text-xl">{failure.title}</h2>
              <p className="py-2">{failure.body}</p>
              {failure.canRetry && (
                <div className="card-actions">
                  <button
                    className="btn btn-primary"
                    disabled={isRetrying}
                    onClick={() => {
                      silentReauthsRef.current = 0
                      failureRef.current = null
                      setIsRetrying(true)
                      void authenticate().finally(() => setIsRetrying(false))
                    }}
                  >
                    {isRetrying ? (
                      <>
                        <span className="loading loading-spinner loading-xs" />
                        Retrying…
                      </>
                    ) : (
                      'Try again'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
